/* eslint-disable react/no-unstable-nested-components */
import {
    Alert,
    Button,
    Card,
    Checkbox,
    DatePicker,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Input,
    Pagination,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SearchInput,
    Table,
    Badge,
} from "@jhool-io/fe-components";
import * as React from "react";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import { IAppCustomDates } from "../../../../utils/types";
import {
    buildSuccessMessageForMatchedPayments,
    convertDateFilterStringToDate,
    displayNameInRightFormat,
    formatDate,
    getTotalAmountOfTableRowsSelected,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    showMoneyInAppFormat,
    truncateString,
} from "../../../../utils/helpers";
import { LISTS_DEFAULT_LIMIT } from "../../../../utils/constants";

import { getPaymentStatusTag } from "../../helpers/payments.helpers";
import {
    ISplitPaymentPayload,
    IUnmatchedPayment,
    PaymentStatus,
} from "../../types/payments.types";
import useToast from "../../../../hooks/useToast";
import { useDebounce } from "../../../../hooks/helpers";
import { useFetchUnmatchedPayment } from "../../hooks/payments.queries";
import ListState from "../../../../components/ListState/ListState";
import {
    useDeleteUnmatchedPayment,
    useMatchPayment,
    useRefreshPaymentStatus,
    useSplitPayment,
} from "../../hooks/payments.mutations";
import { useDoesUserHavePermission } from "../../../../hooks/permissions";
import { UserPermisions } from "../../../../utils/types/user";
import { getBillStatusTag } from "../../../billing/helpers/billing.helpers";
import {
    BillPaymentType,
    BillStatus,
} from "../../../billing/types/billing.types";
import { IBillingResponse } from "../../../../utils/types/billing";
import { useFetchInvoices } from "../../../billing/hooks/queries/billing.queries";
import Loader from "../../../../shared-ui/Loader/Loader";
import { getCptCodeToDisplay } from "../../../notes/helpers/notes.helpers";
import { NoteTypes } from "../../../notes/types/notes.types";

interface UnmatchedPaymentProps {
    setTotalUnMatchedCount: React.Dispatch<React.SetStateAction<string>>;
}

// FUNCTION TO BUILD SUCCESS MESSAGE FOR DELETED PAYMENTS
const buildSuccessMessageForDeletedUnMatchedPayments = (
    deletedPayments: string[],
    undeletedPayments: string[]
) => {
    const deletedPaymentsMessage =
        deletedPayments.length > 0
            ? `${deletedPayments.length} Payments deleted`
            : "";
    const undeletedPaymentMessage =
        undeletedPayments.length > 0
            ? `${undeletedPayments.length} Payments were not deleted`
            : "";
    const message = `Update complete: ${[
        deletedPaymentsMessage,
        undeletedPaymentMessage,
    ]
        .filter(Boolean)
        .join(", ")}`;
    return message;
};

// FUNCTION TO BUILD SUCCESS MESSAGE FOR REFRESHED PAYMENTS
const buildSuccessMessageForRefreshPaymentStatus = (
    updatedPayments: string[],
    unUpdatedPayments: string[]
) => {
    const updatedPaymentMessage =
        updatedPayments.length > 0
            ? `${updatedPayments.length} Bill Status updated`
            : "";
    const unUpdatedPaymentMessage =
        unUpdatedPayments.length > 0
            ? `${unUpdatedPayments.length} Bill Status not updated`
            : "";
    const message = `Update complete: ${[
        updatedPaymentMessage,
        unUpdatedPaymentMessage,
    ]
        .filter(Boolean)
        .join(", ")}`;
    return message;
};

const customDates = [
    IAppCustomDates.ALL_TIME,
    IAppCustomDates.LAST_24_HRS,
    IAppCustomDates.THIS_WEEK,
    IAppCustomDates.LAST_WEEK,
    IAppCustomDates.LAST_TWO_WEEKS,
    IAppCustomDates.THIS_MONTH,
    IAppCustomDates.LAST_MONTH,
];

type Dialogs = "breakdown" | "split_payment";

export default function UnmatchedPaymentsList({
    setTotalUnMatchedCount,
}: UnmatchedPaymentProps) {
    const [searchParams, setSearchParams] = useSearchParams();

    const [rowSelection, setRowSelection] = React.useState({});
    const [breakdownRowSelection, setBreakdownRowSelection] = React.useState(
        {}
    );
    const [getSelectedData, setGetSelectedData] =
        React.useState<Row<IUnmatchedPayment>[]>();
    const [getSelectedDataBreakdown, setGetSelectedDataBreakdown] =
        React.useState<Row<IBillingResponse>[]>();
    const [dialogInview, setDialogInView] = React.useState<Dialogs | null>(
        null
    );
    const [currentUnmatchedPaymentId, setCurrentUnmatchedPaymentId] =
        React.useState<string | null>(null);
    const [sortConfig, setSortConfig] = React.useState<{
        key: string;
        direction: string;
    }>({
        key: "",
        direction: "",
    });

    const breakdownPageFilter = Number(searchParams.get("breakdown_page")) || 1;
    const breakdownLimitFilter =
        Number(searchParams.get("breakdown_limit")) || LISTS_DEFAULT_LIMIT;

    const clientFilter = searchParams.get("search_string") || "";
    const customDateFilter =
        searchParams.get("custom_date") || IAppCustomDates.LAST_24_HRS;
    const invoiceStatusFilter = searchParams.get("invoice_status") || "";
    const fromDateFilter = searchParams.get("from_date");
    const toDateFilter = searchParams.get("to_date");
    const pageFilter = Number(searchParams.get("page")) || 1;
    const limitFilter =
        Number(searchParams.get("limit")) || LISTS_DEFAULT_LIMIT;

    const isPr100Split = searchParams.get("is_pr100_split");
    const paymentTypeFilter = searchParams.get("payment_type") || "Credit";
    const debouncedClientFilter = useDebounce(clientFilter);

    const fromDateFilterToDate = convertDateFilterStringToDate(fromDateFilter);
    const toDateFilterToDate = convertDateFilterStringToDate(toDateFilter);
    const showPatientPortalPaymentsFilter = searchParams.get(
        "show_patient_portal_payments"
    );

    // Mutation hook for splitting payment
    const splitPayment = useSplitPayment();

    const { toast } = useToast();

    // check if logged in user can perform actions
    const canPerformActions = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_PAYMENTS_WRITE,
        UserPermisions.SUPPORT_PAYMENT_CORRECTION_WRITE,
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
    ]);

    const queryClient = useQueryClient();

    const sortableColumns = [
        "client_name",
        "payment_date",
        "payment_amount",
        "non_closed_bill",
    ];

    // React-hook-form for split payment form
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
        watch,
    } = useForm<ISplitPaymentPayload>({
        resolver: yupResolver(
            yup.object().shape({
                invoices: yup.array().of(
                    yup.object().shape({
                        amount: yup
                            .string()
                            .matches(
                                /^(?!^\.)(\d+(\.\d+)?|\.\d+)$/,
                                "Please enter a valid amount"
                            ),
                    })
                ),
            })
        ),
        mode: "onChange",
    });

    const handleConfigAttribute = () => {
        if (sortableColumns.includes(sortConfig.key)) {
            return sortConfig.key;
        }

        return undefined;
    };

    const getPaymetTypeFilter = () => {
        if (paymentTypeFilter === "All") return [];
        if (paymentTypeFilter === "Write-off")
            return [BillPaymentType.WBI, BillPaymentType.WBCI];
        if (paymentTypeFilter === "Refund")
            return [
                BillPaymentType.RI,
                BillPaymentType.RCI,
                BillPaymentType.RIP,
                BillPaymentType.RPR100,
            ];
        return [
            BillPaymentType.I,
            BillPaymentType.CI,
            BillPaymentType.IP,
            BillPaymentType.PR100,
        ];
    };

    const {
        data,
        isLoading: fetchPaymentLoading,
        isSuccess,
        error,
    } = useFetchUnmatchedPayment({
        search_string: debouncedClientFilter,
        custom_date: customDateFilter,
        invoice_status: invoiceStatusFilter,
        from_date: fromDateFilter,
        to_date: toDateFilter,
        page: Number(searchParams.get("page")) || 1,
        limit: LISTS_DEFAULT_LIMIT,
        sort_attribute:
            searchParams.get("sort_attr") || handleConfigAttribute(),
        sort_order: searchParams.get("sort_order") || sortConfig.direction,
        show_patient_portal_payments:
            showPatientPortalPaymentsFilter || undefined,
        payment_type: getPaymetTypeFilter(),
    });

    const paymentInView = data?.data.find(
        (payment) => payment.unmatched_payment_id === currentUnmatchedPaymentId
    );

    const invoicesList = useFetchInvoices(
        {
            client_id: paymentInView?.client_id || undefined,
            client_ids:
                !paymentInView?.client_id &&
                paymentInView?.payment_info?.possible_client_id_matches
                    ? paymentInView.payment_info.possible_client_id_matches
                    : undefined,
            invoice_status: Object.values(BillStatus).filter(
                (status) => status !== BillStatus.CLOSED
            ),
            page: breakdownPageFilter,
            limit: breakdownLimitFilter,
        },
        Boolean(
            paymentInView?.client_id ||
                paymentInView?.payment_info?.possible_client_id_matches
        )
    );

    const deleteUnmatchedPayment = useDeleteUnmatchedPayment();
    const refreshPaymentStatus = useRefreshPaymentStatus();
    const { mutate, isLoading: matchPaymentLoading } = useMatchPayment();

    // watch invoices in split payment form
    const invoices = watch("invoices");

    // get the count of unmatched payment based on the current filter
    if (data) {
        setTotalUnMatchedCount(String(data.total_count));
    }
    if (data && !data.total_count) {
        setTotalUnMatchedCount("0");
    }

    const handleRowClick = (row: Row<IUnmatchedPayment>) => {
        setDialogInView("breakdown");
        setCurrentUnmatchedPaymentId(() => row.original.unmatched_payment_id);
    };

    const handleSetClientFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value === "") searchParams.delete("search_string");
        else searchParams.set("search_string", e.target.value);
        setSearchParams(searchParams);
    };

    const handleCustomDateChange = (value: IAppCustomDates) => {
        searchParams.set("custom_date", value);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleFromDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("from_date");
        else searchParams.set("from_date", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleToDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("to_date");
        else searchParams.set("to_date", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleInvoiceStatusFilterChange = (status: string) => {
        if (status === "") searchParams.delete("invoice_status");
        else searchParams.set("invoice_status", status);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handlePageFilter = (page: number) => {
        searchParams.set("page", String(page));
        setSearchParams(searchParams);
    };

    const handleLimitFilter = (limit: number) => {
        searchParams.set("limit", String(limit));
        setSearchParams(searchParams);
    };

    const getInvoiceFilterTextToDisplay = () => {
        if (invoiceStatusFilter === " has_non_closed_invoice")
            return "Show payments with open bills";
        return "Show payments with no bills";
    };

    const showPatientPortalPaymentsFilterText = () => {
        if (showPatientPortalPaymentsFilter === "true") {
            return "Patient Portal";
        }

        if (showPatientPortalPaymentsFilter === "false") {
            return "Mantle";
        }

        return "Payment Channel";
    };

    const handleShowPatientPortalPaymentsFilter = (payload: string) => {
        if (payload === "") searchParams.delete("show_patient_portal_payments");
        else searchParams.set("show_patient_portal_payments", payload);
        setSearchParams(searchParams);
    };

    const getPaymentTypeFilterText = () => {
        if (paymentTypeFilter === "Write-off") return "Write-off";
        if (paymentTypeFilter === "Refund") return "Refund";
        if (paymentTypeFilter === "Credit") return "Credit";
        return "Payment Type";
    };

    const handlePaymentTypeFilterChange = (type: string) => {
        searchParams.set("payment_type", type);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    // Function to run when trying to close the payment invoice breakdown
    const handleCloseBreakdownModal = () => {
        setCurrentUnmatchedPaymentId(null);
        setBreakdownRowSelection({});
        setGetSelectedDataBreakdown([]);
        setDialogInView(null);
        searchParams.delete("breakdown_page");
        searchParams.delete("breakdown_limit");
        setSearchParams(searchParams);
    };

    const handleBreakdownPageFilter = (page: number) => {
        searchParams.set("breakdown_page", String(page));
        setSearchParams(searchParams);
    };

    const handleBreakdownLimitFilter = (limit: number) => {
        searchParams.set("breakdown_limit", String(limit));
        searchParams.set("breakdown_page", "1");
        setSearchParams(searchParams);
    };

    const handleBreakdownEnableRowSelection = (row: Row<IBillingResponse>) => {
        return (
            row.original.insurance_amount_collected +
                row.original.coinsurance_amount_collected <
            row.original.insurance_amount + row.original.coinsurance_amount
        );
    };

    const handleDeletePayment = () => {
        const selectedIds = getSelectedData
            ? getSelectedData.map((item) => item.original.unmatched_payment_id)
            : [];

        const deletePaymentPayload = {
            unmatched_payment_ids: selectedIds,
        };
        deleteUnmatchedPayment.mutate(deletePaymentPayload, {
            onSuccess: async (response) => {
                await queryClient.invalidateQueries({
                    queryKey: ["unmatched"],
                });

                toast({
                    mode: "success",
                    message: `${buildSuccessMessageForDeletedUnMatchedPayments(
                        response.data.deleted_payments,
                        response.data.undeleted_payments
                    )}`,
                });
                setRowSelection({});
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Cannot delete payment at this time",
                });
            },
        });
    };

    const handleMatchPayment = (
        unmatched_payment_id: string,
        invoiceId: string,
        isPr100?: boolean
    ) => {
        const matchPaymentPayload = {
            unmatched_payment_matches: {
                [invoiceId]: [unmatched_payment_id],
            },
            is_pr_100: isPr100,
        };

        mutate(matchPaymentPayload, {
            onSuccess: async (response) => {
                await queryClient.invalidateQueries({
                    queryKey: ["unmatched"],
                });
                handleCloseBreakdownModal();
                toast({
                    mode: "success",
                    message: `${buildSuccessMessageForMatchedPayments(
                        response.data.added_payments,
                        response.data.unadded_payments
                    )}`,
                });
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not match payment at this time, please try again later",
                });
            },
        });
    };

    // function to update payment status
    const handleRefreshPaymentStatus = () => {
        const selectedIds = getSelectedData
            ? getSelectedData.map((item) => item.original.unmatched_payment_id)
            : [];

        const updatePaymentStatusPayload = {
            payment_ids: selectedIds,
        };
        refreshPaymentStatus.mutate(updatePaymentStatusPayload, {
            onSuccess: async (response) => {
                await queryClient.invalidateQueries({
                    queryKey: ["unmatched"],
                });
                toast({
                    mode: "success",
                    message: `${buildSuccessMessageForRefreshPaymentStatus(
                        response.data.updated_payments,
                        response.data.unupdated_payments
                    )}`,
                });
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "No payment was refreshed",
                });
            },
        });
    };

    // Onsubmit function for split payment form
    const onSplitPaymentFormSubmit = (payload: ISplitPaymentPayload) => {
        const dataToSend = {
            payment_id: currentUnmatchedPaymentId || "",
            invoices: payload.invoices.map((invoice, i) => ({
                amount: Number(invoice.amount),
                invoice_id:
                    getSelectedDataBreakdown?.[i].original.invoice_id || "",
            })),
            payment_type: isPr100Split ? "pr100" : undefined,
        };

        splitPayment.mutate(dataToSend, {
            onSuccess: async (res) => {
                toast({
                    mode: "success",
                    message: res.message || "Payment spliting successful",
                });
                await queryClient.invalidateQueries({
                    queryKey: ["unmatched"],
                });
                searchParams.delete("is_pr100_split");
                setSearchParams(searchParams);
                reset();
                handleCloseBreakdownModal();
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not split payment at this time, please try again later",
                });
            },
        });
    };

    const getDialogTitle = () => {
        if (!paymentInView) return "Open bills";

        const clientId = paymentInView.client_id;
        const clientName = paymentInView.client_name;
        const paymentInfo = paymentInView.payment_info;

        if (!clientId && paymentInfo?.possible_client_id_matches) {
            const matchCount = paymentInfo.possible_client_id_matches.length;
            return `Open bills for ${matchCount} possible client${
                matchCount > 1 ? "s" : ""
            }`;
        }

        return `Open bills for ${makeStringFirstLetterCapital(
            clientName || ""
        )}`;
    };

    const getPaymentSource = (paymentType: string) => {
        if (paymentType === "pr100" || paymentType === "rpr100") {
            return "PR100";
        }
        if (
            paymentType === "i" ||
            paymentType === "wbi" ||
            paymentType === "ri"
        ) {
            return "Insurance";
        }

        if (paymentType === "ip" || paymentType === "rip") {
            return "Interest Payment";
        }

        return "Co-Insurance";
    };

    const getPaymentTypeTag = (type: string) => {
        switch (type) {
            case "wbci":
            case "wbi":
                return (
                    <Badge
                        type="neutral"
                        content={<>Write-off: {getPaymentSource(type)}</>}
                    />
                );
            case "ri":
            case "rci":
            case "rip":
            case "rpr100":
                return (
                    <Badge
                        type="success"
                        content={<>Refund: {getPaymentSource(type)}</>}
                    />
                );
            case "i":
            case "ci":
            case "ip":
            case "pr100":
                return (
                    <Badge
                        type="warning"
                        content={<>Credit: {getPaymentSource(type)}</>}
                    />
                );
            default:
                return <Badge type="neutral" content={type ?? "others"} />;
        }
    };

    // Table columns for main table (unmatched payment table)
    const columns: ColumnDef<IUnmatchedPayment>[] = [
        {
            id: "select",
            header: ({ table }) => (
                <Checkbox
                    handleChange={table.getToggleAllRowsSelectedHandler()}
                    indeterminate={table.getIsSomeRowsSelected()}
                    label=""
                    name="row"
                    value="row"
                    isChecked={table.getIsAllRowsSelected()}
                />
            ),
            cell: ({ row }) => (
                <div>
                    <Checkbox
                        handleChange={row.getToggleSelectedHandler()}
                        indeterminate={row.getIsSomeSelected()}
                        id={row.original.transaction_id}
                        label=""
                        name="row"
                        value="row"
                        isChecked={row.getIsSelected()}
                    />
                </div>
            ),
        },

        {
            accessorKey: "client_name",
            header: "CLIENT",
            cell: ({ row }) => {
                const clientId = row.original.client_id;
                const clientName = row.original.client_name;
                const paymentInfo = row.original.payment_info;

                if (!clientId && paymentInfo?.possible_client_id_matches) {
                    const matchCount =
                        paymentInfo.possible_client_id_matches.length;
                    return (
                        <Badge
                            type="info"
                            className="uppercase"
                            content={`${matchCount} possible client${
                                matchCount > 1 ? "s" : ""
                            }`}
                        />
                    );
                }

                return (
                    <span>
                        {clientName && clientName.trim()
                            ? displayNameInRightFormat(clientName)
                            : "--"}
                    </span>
                );
            },
        },
        {
            accessorKey: "payment_date",
            header: "PAYMENT DATE",
            cell: ({ row }) => formatDate(row.original.payment_date),
        },
        {
            accessorKey: "payment_method",
            header: "PAYMENT METHOD",
            cell: ({ row }) => {
                const cardNumber = row.original.payment_info?.card_number;
                return (
                    <span>
                        {makeStringFirstLetterCapital(
                            row.original.payment_method || "--"
                        )}
                        {(row.original.auth_code ||
                            row.original.cheque_number) && (
                            <span>
                                {" "}
                                -{" "}
                                {row.original.auth_code ||
                                    row.original.cheque_number ||
                                    "--"}
                            </span>
                        )}
                        {row.original.payment_method === "card" &&
                        cardNumber ? (
                            <span> / **** {cardNumber}</span>
                        ) : null}
                    </span>
                );
            },
        },
        {
            accessorKey: "payment_type",
            header: "PAYMENT TYPE",
            cell: ({ row }) =>
                row.original.payment_type
                    ? getPaymentTypeTag(row.original.payment_type)
                    : "--",
        },
        {
            accessorKey: "payment_status",
            header: "PAYMENT STATUS",
            cell: ({ row }) =>
                getPaymentStatusTag(
                    row.original.payment_status as PaymentStatus
                ),
        },
        {
            accessorKey: "start_date_time",
            header: "DESCRIPTION",
            cell: ({ row }) =>
                row.original?.start_date_time
                    ? `Payment for ${formatDate(row.original.start_date_time)}`
                    : "--",
        },
        {
            accessorKey: "processor_payment_status",
            header: "PROCESSOR STATUS",
            cell: ({ row }) =>
                makeStringFirstLetterCapital(
                    row.original.processor_payment_status || "--"
                ),
        },
        {
            accessorKey: "payment_amount",
            header: "PAYMENT AMOUNT",
            cell: ({ row }) =>
                showMoneyInAppFormat(row.original.amount) || "--",
        },
        {
            accessorKey: "non_closed_bill",
            header: "OPEN BILL",
            cell: ({ row }) => row.original.non_closed_invoice || "--",
        },
    ];

    // Columns for each payment breakdown table
    const breakdownColumns: ColumnDef<IBillingResponse>[] = [
        {
            id: "select",
            header: ({ table }) => (
                <Checkbox
                    handleChange={table.getToggleAllRowsSelectedHandler()}
                    indeterminate={table.getIsSomeRowsSelected()}
                    label=""
                    name="row"
                    value="row"
                    isChecked={table.getIsAllRowsSelected()}
                />
            ),
            cell: ({ row }) => (
                <div
                    hidden={
                        row.original.coinsurance_amount_collected +
                            row.original.insurance_amount_collected >=
                        row.original.insurance_amount +
                            row.original.coinsurance_amount
                    }
                >
                    <Checkbox
                        handleChange={row.getToggleSelectedHandler()}
                        indeterminate={row.getIsSomeSelected()}
                        id={row.original.invoice_id}
                        label=""
                        name="row"
                        value="row"
                        isChecked={row.getIsSelected()}
                    />
                </div>
            ),
        },
        {
            accessorKey: "date_of_service",
            header: "DATE OF SERVICE",
            cell: ({ row }) => formatDate(row.original.date_of_service) || "--",
        },
        {
            accessorKey: "client",
            header: "CLIENT",
            cell: ({ row }) =>
                row.original.client.first_name
                    ? displayNameInRightFormat({
                          firstName: row.original.client.first_name,
                          lastName: row.original.client.last_name,
                      })
                    : "--",
        },
        {
            accessorKey: "provider_name",
            header: "Provider",
            cell: ({ row }) =>
                row.original.provider.first_name
                    ? displayNameInRightFormat({
                          firstName: row.original.provider.first_name,
                          lastName: row.original.provider.last_name,
                      })
                    : "--",
        },
        {
            accessorKey: "cpt_code",
            header: "Cpt Code",
            cell: ({ row }) =>
                getCptCodeToDisplay({
                    noteType: row.original.note_type as NoteTypes,
                    cptCode: row.original.cpt_code,
                    unitCalculation:
                        row.original.note_metadata?.unit_calculation,
                }),
        },
        {
            accessorKey: "insurance_amount",
            header: "Amount charged",
            cell: ({ row }) =>
                showMoneyInAppFormat(
                    row.original.insurance_amount +
                        row.original.coinsurance_amount
                ) || "--",
        },
        {
            accessorKey: "coinsurance_amount_collected",
            header: "Amount collected",
            cell: ({ row }) =>
                showMoneyInAppFormat(
                    row.original.coinsurance_amount_collected +
                        row.original.insurance_amount_collected
                ) || "--",
        },
        {
            accessorKey: "invoice_status",
            header: "Bill Status",
            cell: ({ row }) =>
                getBillStatusTag(row.original.invoice_status as BillStatus),
        },
    ];

    return (
        <>
            <Dialog open={dialogInview === "split_payment"}>
                <DialogContent
                    title={`Split payment as ${
                        isPr100Split ? "PR100" : "coinsurance"
                    }`}
                    handleCloseDialog={() => {
                        setDialogInView("breakdown");
                        searchParams.delete("is_pr100_split");
                        setSearchParams(searchParams);
                        reset();
                    }}
                    variant="center"
                    showFooter
                    saveText="Split payment"
                    cancelText="Cancel"
                    submitBtnFormValue="split-payment"
                    isSubmitBtnDisabled={
                        splitPayment.isLoading ||
                        (invoices &&
                            invoices.reduce(
                                (accumulator, currentValue) =>
                                    accumulator + Number(currentValue.amount),
                                0
                            ) !== paymentInView?.amount)
                    }
                    isCancelBtnDisabled={
                        splitPayment.isLoading ||
                        (invoices &&
                            invoices.reduce(
                                (accumulator, currentValue) =>
                                    accumulator + currentValue.amount,
                                0
                            ) !== paymentInView?.amount)
                    }
                    classNames="px-0"
                >
                    <>
                        <div className="px-[--padding-card-x-lg]">
                            <Alert
                                type="info"
                                title="Split payment on selected bills"
                                description={`Total amount of bills selected to split payment across must equal ${
                                    paymentInView
                                        ? showMoneyInAppFormat(
                                              paymentInView.amount
                                          )
                                        : "Payment amount"
                                }`}
                            />
                        </div>
                        <div className="mt-[--spacing-header]">
                            <p className="text-caption font-medium px-[--padding-card-x-lg] mb-[--spacing-items]">
                                Input amount to split across bills
                            </p>
                            <form
                                onSubmit={handleSubmit(
                                    onSplitPaymentFormSubmit
                                )}
                                id="split-payment"
                            >
                                {getSelectedDataBreakdown?.map(
                                    (
                                        invoice: Row<IBillingResponse>,
                                        i: number
                                    ) => {
                                        const balance =
                                            invoice.original.insurance_amount +
                                                invoice.original
                                                    .coinsurance_amount -
                                                (invoice.original
                                                    .coinsurance_amount_collected +
                                                    invoice.original
                                                        .insurance_amount_collected) <=
                                            0
                                                ? 0
                                                : invoice.original
                                                      .insurance_amount +
                                                  invoice.original
                                                      .coinsurance_amount -
                                                  (invoice.original
                                                      .coinsurance_amount_collected +
                                                      invoice.original
                                                          .insurance_amount_collected);
                                        return (
                                            <div
                                                className="px-[--padding-card-x-lg] py-[--padding-card-y-lg] [border-top:1px_solid_var(--color-stroke)]"
                                                key={
                                                    invoice.original.invoice_id
                                                }
                                            >
                                                <div className="flex justify-between text-caption font-semibold items-center mb-[--spacing-items]">
                                                    <p className="text-text">
                                                        {`Open Bill: ${formatDate(
                                                            invoice.original
                                                                .date_of_service
                                                        )}`}
                                                    </p>
                                                    <p className="text-danger">
                                                        {`Balance:
                                                    ${showMoneyInAppFormat(
                                                        balance
                                                    )}`}
                                                    </p>
                                                </div>
                                                <Input
                                                    {...register(
                                                        `invoices.${i}.amount`
                                                    )}
                                                    label="Split amount"
                                                    hasError={
                                                        !!errors?.invoices?.[i]
                                                            ?.amount
                                                    }
                                                    errorText={
                                                        errors?.invoices?.[i]
                                                            ?.amount?.message
                                                    }
                                                    showCurrency
                                                />
                                            </div>
                                        );
                                    }
                                )}
                            </form>
                        </div>
                    </>
                </DialogContent>
            </Dialog>
            <Dialog open={dialogInview === "breakdown"}>
                <DialogContent
                    title={getDialogTitle()}
                    showFooter={false}
                    handleCloseDialog={handleCloseBreakdownModal}
                    wrapperClass="w-[850px]"
                >
                    <>
                        {paymentInView && (
                            <div className="mb-[--spacing-header] grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-[--spacing-items] bg-[--card-bg-gray] rounded-md [border:1px_solid_var(--color-stroke)] border border-strokedark px-[--padding-card-x] py-[--padding-card-y]">
                                <div>
                                    <p className="section-info">Payment Date</p>
                                    <p className="section-value">
                                        {`${formatDate(
                                            paymentInView.payment_date,
                                            false,
                                            "MM/dd/yyyy - h:mmaaa"
                                        )}` || "--"}
                                    </p>
                                </div>
                                <div>
                                    <p className="section-info">
                                        Payment method
                                    </p>
                                    <p className="section-value">
                                        {`${makeStringFirstLetterCapital(
                                            paymentInView.payment_method || "--"
                                        )} -  ${
                                            paymentInView.auth_code ||
                                            paymentInView.cheque_number ||
                                            "--"
                                        }${
                                            paymentInView.payment_method ===
                                                "card" &&
                                            paymentInView.payment_info
                                                ?.card_number
                                                ? ` / **** ${paymentInView.payment_info.card_number}`
                                                : ""
                                        }`}
                                    </p>
                                </div>
                                <div>
                                    <p className="section-info">Payment type</p>
                                    <p className="section-value">
                                        {paymentInView.payment_type
                                            ? getPaymentTypeTag(
                                                  paymentInView.payment_type
                                              )
                                            : "--"}
                                    </p>
                                </div>
                                <div>
                                    <p className="section-info">
                                        Payment amount
                                    </p>
                                    <p className="section-value">
                                        {showMoneyInAppFormat(
                                            paymentInView.amount
                                        ) || "--"}
                                    </p>
                                </div>
                                <div>
                                    <p className="section-info">Open bills</p>
                                    <p className="section-value">
                                        {paymentInView.non_closed_invoice ||
                                            "--"}
                                    </p>
                                </div>
                            </div>
                        )}
                        <div className="flex justify-end mb-[--spacing-items]">
                            {canPerformActions &&
                                getSelectedDataBreakdown &&
                                getSelectedDataBreakdown.length === 1 && (
                                    <div className="flex items-center gap-[--spacing-items] flex-wrap">
                                        <Button
                                            disabled={matchPaymentLoading}
                                            onClick={() =>
                                                handleMatchPayment(
                                                    currentUnmatchedPaymentId as string,
                                                    getSelectedDataBreakdown[0]
                                                        .original.invoice_id ||
                                                        ""
                                                )
                                            }
                                        >
                                            Match as coinsurance
                                        </Button>
                                        <Button
                                            disabled={matchPaymentLoading}
                                            onClick={() =>
                                                handleMatchPayment(
                                                    currentUnmatchedPaymentId as string,
                                                    getSelectedDataBreakdown[0]
                                                        .original.invoice_id ||
                                                        "",
                                                    true
                                                )
                                            }
                                        >
                                            Match as PR100
                                        </Button>
                                    </div>
                                )}
                            {canPerformActions &&
                                getSelectedDataBreakdown &&
                                getSelectedDataBreakdown.length > 1 && (
                                    <div className="flex items-center gap-[--spacing-items] flex-wrap">
                                        <Button
                                            onClick={() =>
                                                setDialogInView("split_payment")
                                            }
                                        >
                                            Split payment as coinsurance
                                        </Button>
                                        <Button
                                            onClick={() => {
                                                setDialogInView(
                                                    "split_payment"
                                                );
                                                searchParams.set(
                                                    "is_pr100_split",
                                                    "true"
                                                );
                                                setSearchParams(searchParams);
                                            }}
                                        >
                                            Split payment as PR100
                                        </Button>
                                    </div>
                                )}
                        </div>
                        {invoicesList.isLoading && <Loader />}
                        {invoicesList.error && (
                            <ListState
                                isError
                                stateHelperText="Try reloading this page to solve this issue"
                                errorMsg={
                                    error?.response?.data.message ||
                                    `Cannot display client invoices at this time please try again later`
                                }
                            />
                        )}
                        {invoicesList.data &&
                            invoicesList.isSuccess &&
                            invoicesList.data.data.length === 0 && (
                                <ListState
                                    stateHelperText="Client invoices list will appear here"
                                    isError={false}
                                    emptyMessage="There are no client invoice yet"
                                />
                            )}
                        {invoicesList.data &&
                            invoicesList.isSuccess &&
                            invoicesList.data.data.length > 0 && (
                                <Table
                                    columns={breakdownColumns}
                                    data={invoicesList.data.data || []}
                                    rowSelection={breakdownRowSelection}
                                    setRowSelection={setBreakdownRowSelection}
                                    setGetSelectedData={
                                        setGetSelectedDataBreakdown
                                    }
                                    enableRowSelection={
                                        handleBreakdownEnableRowSelection
                                    }
                                    hasPagination={
                                        invoicesList.data.total_count > 20
                                    }
                                    pagination={
                                        <Pagination
                                            totalCount={
                                                invoicesList.data.total_count
                                            }
                                            totalPages={
                                                invoicesList.data.total_pages
                                            }
                                            currentPage={breakdownPageFilter}
                                            onPageChange={
                                                handleBreakdownPageFilter
                                            }
                                            limit={breakdownLimitFilter}
                                            onLimitChange={
                                                handleBreakdownLimitFilter
                                            }
                                        />
                                    }
                                />
                            )}
                    </>
                </DialogContent>
            </Dialog>
            <Card classNames="mb-[--spacing-header]">
                <div className="flex flex-col gap-y-[--spacing-items]">
                    <SearchInput
                        containerClass="w-[400px]"
                        placeholder="Search by client name or chart number"
                        onChange={handleSetClientFilter}
                        defaultValue={clientFilter}
                    />
                    <div className="flex gap-x-[--spacing-items] items-center">
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <FilterButton className="capitalize">
                                    {customDateFilter
                                        ? truncateString(
                                              removeEnumUnderscore(
                                                  customDateFilter
                                              )
                                          )
                                        : "Date range"}
                                </FilterButton>
                            </DropdownTrigger>
                            <DropdownContent align="end" width={200}>
                                {customDates.map((range) => (
                                    <DropdownItem
                                        isSelected={customDateFilter === range}
                                        key={range}
                                        onClick={() =>
                                            handleCustomDateChange(range)
                                        }
                                    >
                                        {removeEnumUnderscore(range)}{" "}
                                    </DropdownItem>
                                ))}
                            </DropdownContent>
                        </Dropdown>
                        <Popover>
                            <PopoverTrigger asChild>
                                <FilterButton>Payment Date</FilterButton>
                            </PopoverTrigger>
                            <PopoverContent align="end">
                                <div className="mb-1">
                                    <DatePicker
                                        label="From"
                                        placeholderText="MM/DD/YYYY"
                                        selected={fromDateFilterToDate}
                                        onChange={handleFromDateFilterChange}
                                        maxDate={
                                            toDateFilterToDate ||
                                            new Date(Date.now())
                                        }
                                        isClearable
                                    />
                                </div>
                                <DatePicker
                                    label="To"
                                    placeholderText="MM/DD/YYYY"
                                    selected={toDateFilterToDate}
                                    onChange={handleToDateFilterChange}
                                    minDate={fromDateFilterToDate}
                                    isClearable
                                />
                            </PopoverContent>
                        </Popover>
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <FilterButton>
                                    {invoiceStatusFilter
                                        ? truncateString(
                                              getInvoiceFilterTextToDisplay()
                                          )
                                        : "Show all bills"}
                                </FilterButton>
                            </DropdownTrigger>
                            <DropdownContent align="end" width="auto">
                                <DropdownItem
                                    isSelected={!invoiceStatusFilter}
                                    onClick={() =>
                                        handleInvoiceStatusFilterChange("")
                                    }
                                >
                                    Show all bills
                                </DropdownItem>
                                <DropdownItem
                                    isSelected={
                                        invoiceStatusFilter ===
                                        "has_non_closed_invoice"
                                    }
                                    onClick={() =>
                                        handleInvoiceStatusFilterChange(
                                            "has_non_closed_invoice"
                                        )
                                    }
                                >
                                    Show payments with open bills
                                </DropdownItem>
                                <DropdownItem
                                    isSelected={
                                        invoiceStatusFilter === "has_no_invoice"
                                    }
                                    onClick={() =>
                                        handleInvoiceStatusFilterChange(
                                            "has_no_invoice"
                                        )
                                    }
                                >
                                    Show payments with no bills
                                </DropdownItem>
                            </DropdownContent>
                        </Dropdown>
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <FilterButton>
                                    {getPaymentTypeFilterText()}
                                </FilterButton>
                            </DropdownTrigger>

                            <DropdownContent width={250}>
                                <DropdownItem
                                    onClick={() =>
                                        handlePaymentTypeFilterChange("All")
                                    }
                                    isSelected={paymentTypeFilter === "All"}
                                >
                                    All
                                </DropdownItem>
                                <DropdownItem
                                    onClick={() =>
                                        handlePaymentTypeFilterChange("Credit")
                                    }
                                    isSelected={paymentTypeFilter === "Credit"}
                                >
                                    Credit
                                </DropdownItem>
                                <DropdownItem
                                    onClick={() =>
                                        handlePaymentTypeFilterChange(
                                            "Write-off"
                                        )
                                    }
                                    isSelected={
                                        paymentTypeFilter === "Write-off"
                                    }
                                >
                                    Write-off
                                </DropdownItem>
                                <DropdownItem
                                    onClick={() =>
                                        handlePaymentTypeFilterChange("Refund")
                                    }
                                    isSelected={paymentTypeFilter === "Refund"}
                                >
                                    Refund
                                </DropdownItem>
                            </DropdownContent>
                        </Dropdown>
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <FilterButton>
                                    {showPatientPortalPaymentsFilterText()}
                                </FilterButton>
                            </DropdownTrigger>
                            <DropdownContent width={250}>
                                <DropdownItem
                                    onClick={() =>
                                        handleShowPatientPortalPaymentsFilter(
                                            ""
                                        )
                                    }
                                    isSelected={
                                        !showPatientPortalPaymentsFilter
                                    }
                                >
                                    All
                                </DropdownItem>
                                <DropdownItem
                                    onClick={() =>
                                        handleShowPatientPortalPaymentsFilter(
                                            "false"
                                        )
                                    }
                                    isSelected={
                                        showPatientPortalPaymentsFilter ===
                                        "false"
                                    }
                                >
                                    Mantle
                                </DropdownItem>
                                <DropdownItem
                                    onClick={() =>
                                        handleShowPatientPortalPaymentsFilter(
                                            "true"
                                        )
                                    }
                                    isSelected={
                                        showPatientPortalPaymentsFilter ===
                                        "true"
                                    }
                                >
                                    Patient Portal
                                </DropdownItem>
                            </DropdownContent>
                        </Dropdown>
                    </div>
                </div>
            </Card>
            <div>
                {canPerformActions && (
                    <div>
                        {getTotalAmountOfTableRowsSelected(rowSelection)
                            .amount > 0 && (
                            <div className="flex items-center mb-[--spacing-items] gap-[--spacing-items] flex-wrap">
                                <div className="font-semibold text-base">
                                    {
                                        getTotalAmountOfTableRowsSelected(
                                            rowSelection
                                        ).amountString
                                    }{" "}
                                    :
                                </div>{" "}
                                <Button
                                    className="bg-danger text-white hover:bg-danger"
                                    onClick={handleDeletePayment}
                                    disabled={deleteUnmatchedPayment.isLoading}
                                >
                                    Delete Unmatched Payments
                                </Button>
                                <Button
                                    variant="secondary"
                                    onClick={handleRefreshPaymentStatus}
                                    disabled={refreshPaymentStatus.isLoading}
                                >
                                    Refresh payment status
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </div>
            {fetchPaymentLoading && <Loader />}
            {error && (
                <ListState
                    isError
                    stateHelperText="Try reloading this page to solve this issue"
                    errorMsg={
                        error?.response?.data.message ||
                        `Cannot display unmatched payments at this time please try again later`
                    }
                />
            )}
            {data && isSuccess && data.data.length === 0 && (
                <ListState
                    stateHelperText="UnMatched payment list will appear here"
                    isError={false}
                    emptyMessage="There are no unmatched payments yet"
                />
            )}
            {isSuccess && data && data.data.length > 0 && (
                <div>
                    <Table
                        columns={columns}
                        data={data.data}
                        rowSelection={rowSelection}
                        setRowSelection={setRowSelection}
                        handleRowClick={handleRowClick}
                        setGetSelectedData={setGetSelectedData}
                        hasPagination={data.total_count > 20}
                        setSortConfig={setSortConfig}
                        sortConfig={sortConfig}
                        sortableColumns={sortableColumns}
                        pagination={
                            <Pagination
                                totalCount={data.total_count}
                                totalPages={data.total_pages}
                                currentPage={pageFilter}
                                onPageChange={handlePageFilter}
                                limit={limitFilter}
                                onLimitChange={handleLimitFilter}
                            />
                        }
                    />
                </div>
            )}
        </>
    );
}
